
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";
import { useToast } from "@/hooks/use-toast";

interface AddOrderDialogProps {
  onOrderAdd: (newOrder: EtsyOrder) => void;
}

const AddOrderDialog = ({ onOrderAdd }: AddOrderDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  
  const [newOrder, setNewOrder] = useState<EtsyOrder>({
    receiptId: '',
    trackingId: '',
    customerName: '',
    productName: '',
    city: '',
    zip: '',
    address: '',
    country: '',
    date: new Date().toISOString().split('T')[0],
    status: 'new',
    totalAmount: 0,
    subtotalAmount: 0,
    currencyCode: 'USD',
    customerEmail: '',
  });

  const handleSave = () => {
    if (!newOrder.receiptId || !newOrder.customerName || !newOrder.productName) {
      toast({
        title: "Missing Information",
        description: "Please fill in Receipt ID, Customer Name, and Product Name",
        variant: "destructive",
      });
      return;
    }

    // Generate a unique receipt ID with timestamp if not provided
    const orderToAdd = {
      ...newOrder,
      receiptId: newOrder.receiptId || `MANUAL-${Date.now()}`,
    };

    onOrderAdd(orderToAdd);
    setIsOpen(false);
    
    // Reset form
    setNewOrder({
      receiptId: '',
      trackingId: '',
      customerName: '',
      productName: '',
      city: '',
      zip: '',
      address: '',
      country: '',
      date: new Date().toISOString().split('T')[0],
      status: 'new',
      totalAmount: 0,
      subtotalAmount: 0,
      currencyCode: 'USD',
      customerEmail: '',
    });

    toast({
      title: "Order Added",
      description: "Manual order has been successfully added",
    });
  };

  const handleInputChange = (field: keyof EtsyOrder, value: string | number) => {
    setNewOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Add Order
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Manual Order</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="receiptId">Receipt ID *</Label>
              <Input
                id="receiptId"
                value={newOrder.receiptId}
                onChange={(e) => handleInputChange('receiptId', e.target.value)}
                placeholder="Enter receipt ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trackingId">Tracking ID</Label>
              <Input
                id="trackingId"
                value={newOrder.trackingId}
                onChange={(e) => handleInputChange('trackingId', e.target.value)}
                placeholder="Enter tracking ID"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name *</Label>
              <Input
                id="customerName"
                value={newOrder.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                placeholder="Enter customer name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">Customer Email</Label>
              <Input
                id="customerEmail"
                type="email"
                value={newOrder.customerEmail || ''}
                onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                placeholder="customer@email.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="productName">Product Name *</Label>
            <Input
              id="productName"
              value={newOrder.productName}
              onChange={(e) => handleInputChange('productName', e.target.value)}
              placeholder="Enter product name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={newOrder.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Street address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={newOrder.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="City"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP Code</Label>
              <Input
                id="zip"
                value={newOrder.zip}
                onChange={(e) => handleInputChange('zip', e.target.value)}
                placeholder="ZIP code"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={newOrder.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="Country"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={newOrder.status} onValueChange={(value) => handleInputChange('status', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Order Date</Label>
              <Input
                id="date"
                type="date"
                value={newOrder.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="totalAmount">Total Amount</Label>
              <Input
                id="totalAmount"
                type="number"
                step="0.01"
                value={newOrder.totalAmount || ''}
                onChange={(e) => handleInputChange('totalAmount', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subtotalAmount">Net Revenue</Label>
              <Input
                id="subtotalAmount"
                type="number"
                step="0.01"
                value={newOrder.subtotalAmount || ''}
                onChange={(e) => handleInputChange('subtotalAmount', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="currencyCode">Currency Code</Label>
            <Input
              id="currencyCode"
              value={newOrder.currencyCode || 'USD'}
              onChange={(e) => handleInputChange('currencyCode', e.target.value)}
              placeholder="USD"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Add Order
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddOrderDialog;
